import { CASE_STUDIES_ENABLED } from "@/config/features";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import logoWhite from "@/assets/beedigit_logo_black_long.svg";
import { useModules } from "@/hooks/useCatalog";
import { useIndustryList } from "@/hooks/useIndustryList";
import { track } from "@/lib/analytics";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Sheet, SheetContent, SheetTitle, SheetTrigger, SheetClose,
} from "@/components/ui/sheet";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

type NavItem = { path: string; label: string };


const resources: NavItem[] = [
  { path: "/blog", label: "Blog" },
  ...(CASE_STUDIES_ENABLED ? [{ path: "/case-studies", label: "Case Studies" }] : []),
  { path: "/news", label: "News" },
  { path: "/glossary", label: "Glossary" },
  { path: "/siri", label: "SIRI Certification" },
  { path: "/roi-calculator", label: "ROI Calculator" },
];

const company: NavItem[] = [
  { path: "/about", label: "About" },
  { path: "/partners", label: "Partners" },
  { path: "/contact", label: "Contact" },
];

const norm = (p: string) => {
  const clean = (p || "/").split("?")[0].split("#")[0];
  return clean.length > 1 ? clean.replace(/\/+$/, "") : "/";
};

const linkBase =
  "relative inline-flex items-center whitespace-nowrap text-[0.9rem] font-medium transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm";
const linkIdle = "text-foreground/75 hover:text-foreground";
const linkActive =
  "text-primary after:absolute after:left-0 after:right-0 after:-bottom-1.5 after:h-0.5 after:rounded-full after:bg-primary";

const itemBase =
  "block rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const itemIdle = "text-foreground/80 hover:bg-accent hover:text-accent-foreground";
const itemActive = "bg-primary/10 text-primary font-semibold";

const Navigation = () => {
  const modules: NavItem[] = useModules().modules.map((m) => ({ path: `/modules/${m.slug}`, label: m.title.split(" — ")[0] }));
  const industries: NavItem[] = useIndustryList().map((i) => ({ path: `/industries/${i.slug}`, label: i.title }));
  const { t } = useLanguage();
  const location = useLocation();
  const { basePath } = useLanguage();
  const current = norm(basePath);

  const isExact = useCallback((path: string) => current === norm(path), [current]);
  const isWithin = useCallback(
    (prefix: string) => {
      const p = norm(prefix);
      return current === p || current.startsWith(`${p}/`);
    },
    [current],
  );
  const groupActive = useCallback(
    (items: NavItem[]) => items.some((i) => isExact(i.path)),
    [isExact],
  );

  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLAnchorElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);

  // ---- Fit-based breakpoint: switch to the compact menu only when the full
  // navigation (in the current language) no longer fits the header row.
  useLayoutEffect(() => {
    const measure = () => {
      const row = rowRef.current, nav = navRef.current, logo = logoRef.current, rail = railRef.current;
      if (!row || !nav || !logo || !rail) return;
      const available = row.clientWidth - logo.offsetWidth - rail.offsetWidth - 56;
      setCompact(nav.scrollWidth > available);

    };
    measure();
    const ro = new ResizeObserver(measure);
    if (rowRef.current) ro.observe(rowRef.current);
    if (navRef.current) ro.observe(navRef.current);
    window.addEventListener("resize", measure);
    const id = window.setTimeout(measure, 300);
    document.fonts?.ready.then(measure).catch(() => {}); // re-measure after webfonts
    return () => { ro.disconnect(); window.removeEventListener("resize", measure); window.clearTimeout(id); };
  }, [t]);

  // Close everything on navigation
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Outside click + Escape for desktop dropdowns
  useEffect(() => {
    if (!openMenu) return;
    const onDown = (e: PointerEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setOpenMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDown); document.removeEventListener("keydown", onKey); };
  }, [openMenu]);

  const defaultAccordion = useMemo(() => {
    if (isWithin("/modules")) return ["modules"];
    if (isWithin("/industries")) return ["industries"];
    if (groupActive(resources)) return ["resources"];
    if (groupActive(company)) return ["company"];
    return [];
  }, [isWithin, groupActive]);

  const TopLink = ({ path, label }: NavItem) => {
    const active = isExact(path);
    return (
      <Link
        to={path}
        aria-current={active ? "page" : undefined}
        className={cn(linkBase, active ? linkActive : linkIdle)}
      >
        {t(label)}
      </Link>
    );
  };

  const Dropdown = ({ id, label, items, href }: { id: string; label: string; items: NavItem[]; href?: string }) => {
    const open = openMenu === id;
    const active = href ? isWithin(href) : groupActive(items);
    return (
      <div
        className="relative"
        onMouseEnter={() => setOpenMenu(id)}
        onMouseLeave={() => setOpenMenu((cur) => (cur === id ? null : cur))}
      >
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="true"
          onClick={() => setOpenMenu(open ? null : id)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setOpenMenu(id); }
          }}
          className={cn(linkBase, "gap-1", active ? linkActive : linkIdle)}
        >
          {label}
          <ChevronDown className={cn("h-3.5 w-3.5 opacity-60 transition-transform", open && "rotate-180")} />
        </button>
        {/* pt-2 keeps a hover bridge between trigger and panel (no pointer gap) */}
        <div className={cn("absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50", open ? "block" : "hidden")}>
          <div className="max-h-[70vh] w-[min(20rem,90vw)] overflow-y-auto overscroll-contain rounded-lg border border-border bg-popover p-2 shadow-lg">
            {href && (
              <Link
                to={href}
                aria-current={isExact(href) ? "page" : undefined}
                className={cn(itemBase, "mb-1 font-semibold", isExact(href) ? itemActive : itemIdle)}
              >
                {t("Overview")}
              </Link>
            )}
            {items.map((i) => {
              const a = isExact(i.path);
              return (
                <Link
                  key={i.path}
                  to={i.path}
                  aria-current={a ? "page" : undefined}
                  className={cn(itemBase, a ? itemActive : itemIdle)}
                >
                  {t(i.label)}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const MobileLink = ({ path, label, indent }: NavItem & { indent?: boolean }) => {
    const active = isExact(path);
    return (
      <SheetClose asChild>
        <Link
          to={path}
          aria-current={active ? "page" : undefined}
          className={cn(
            "flex min-h-[44px] items-center rounded-md px-3 text-[0.95rem] transition-colors",
            indent && "ps-6",
            active ? itemActive : "text-foreground/85 hover:bg-accent",
          )}
        >
          {t(label)}
        </Link>
      </SheetClose>
    );
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl">
      <div className="container mx-auto px-4">
        <div ref={rowRef} className="flex h-14 items-center gap-4">
          <Link ref={logoRef} to="/" className="flex shrink-0 items-center" aria-label="BEEDIGIT">
            <img
              src={logoWhite}
              alt={t("BEEDIGIT manufacturing platform logo")}
              className="h-8 w-auto shrink-0"
              width={140}
              height={32}
              decoding="async"
              fetchPriority="high"
            />
          </Link>

          {/* Primary navigation — kept mounted (off-screen when compact) so its
              natural width can be measured for the fit-based breakpoint. */}
          <div
            ref={navRef}
            className={cn(
              "items-center gap-x-4",
              compact
                ? "pointer-events-none invisible absolute -left-[9999px] top-0 flex"
                : "mx-auto flex shrink-0",
            )}
            aria-hidden={compact}
          >
            <TopLink path="/platform" label="How it works" />
            <Dropdown id="modules" label={t("Modules")} items={modules} href="/modules" />
            <Dropdown id="industries" label={t("Industries")} items={industries} href="/industries" />
            <TopLink path="/hardware" label="Hardware" />
            <TopLink path="/integrations" label="Integrations" />
            <TopLink path="/lean" label="Lean & CI" />
            <TopLink path="/pricing" label="Pricing" />
            <Dropdown id="resources" label={t("Resources")} items={resources} />
            <Dropdown id="company" label={t("Company")} items={company} />
          </div>

          <div className="ms-auto flex shrink-0 items-center gap-2 justify-end">
            {/* Desktop rail stays mounted (off-screen when compact) so the
                fit measurement is stable and cannot oscillate. */}
            <div
              ref={railRef}
              className={cn(
                "items-center gap-2",
                compact ? "pointer-events-none invisible absolute -left-[9999px] top-0 flex" : "flex",
              )}
              aria-hidden={compact}
            >
              <LanguageSwitcher />
              <Button size="sm" variant="outline" asChild>
                <a href="https://my.beedigit.com" target="_blank" rel="noopener noreferrer">
                  {t("Sign in")}
                </a>
              </Button>
              <Button size="sm" asChild className="shadow-glow-primary">
                <Link to="/get-started" onClick={() => track("cta_book_assessment_click", { location: "nav" })}>
                  {t("Contact Us")}
                </Link>
              </Button>
            </div>

            {compact && (

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={t("Menu")}>
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="flex w-full flex-col gap-0 p-0 sm:max-w-md [&>button]:hidden"
                  style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
                >
                  <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
                    <SheetTitle className="text-sm font-semibold">{t("Menu")}</SheetTitle>
                    <SheetClose asChild>
                      <Button variant="ghost" size="icon" className="h-11 w-11" aria-label={t("Close")}>
                        <X className="h-5 w-5" />
                      </Button>
                    </SheetClose>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-3">
                    <MobileLink path="/platform" label="How it works" />
                    <Accordion type="multiple" defaultValue={defaultAccordion}>
                      {[
                        { id: "modules", label: "Modules", items: modules, href: "/modules" },
                        { id: "industries", label: "Industries", items: industries, href: "/industries" },
                        { id: "resources", label: "Resources", items: resources },
                        { id: "company", label: "Company", items: company },
                      ].map((g) => (
                        <AccordionItem key={g.id} value={g.id} className="border-none">
                          <AccordionTrigger
                            className={cn(
                              "min-h-[44px] rounded-md px-3 py-0 text-[0.95rem] font-medium hover:no-underline hover:bg-accent",
                              (g.href ? isWithin(g.href) : groupActive(g.items)) && "text-primary",
                            )}
                          >
                            {t(g.label)}
                          </AccordionTrigger>
                          <AccordionContent className="pb-1">
                            {g.href && <MobileLink path={g.href} label="Overview" indent />}
                            {g.items.map((i) => (
                              <MobileLink key={i.path} path={i.path} label={i.label} indent />
                            ))}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                    <MobileLink path="/hardware" label="Hardware" />
                    <MobileLink path="/integrations" label="Integrations" />
                    <MobileLink path="/lean" label="Lean & CI" />
                    <MobileLink path="/pricing" label="Pricing" />
                  </div>

                  <div className="shrink-0 space-y-2 border-t border-border p-4">
                    <LanguageSwitcher />
                    <Button variant="outline" className="h-11 w-full" asChild>
                      <a href="https://my.beedigit.com" target="_blank" rel="noopener noreferrer">
                        {t("Sign in")}
                      </a>
                    </Button>
                    <SheetClose asChild>
                      <Button className="h-11 w-full" asChild>
                        <Link to="/get-started" onClick={() => track("cta_book_assessment_click", { location: "nav_mobile" })}>
                          {t("Contact Us")}
                        </Link>
                      </Button>
                    </SheetClose>
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
